import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Send, ArrowRight } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

const INLINE_MD_COMPONENTS = {
    p: ({ children }) => <>{children}</>,
    a: ({ node, ...props }) => (
        <a
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline break-all"
            {...props}
        />
    ),
    code: ({ children }) => (
        <code className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-1 py-0.5 rounded text-[0.9em] font-mono">
            {children}
        </code>
    ),
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    ul: ({ children }) => <>{children}</>,
    ol: ({ children }) => <>{children}</>,
    li: ({ children }) => <span className="mr-2">• {children}</span>,
    h1: ({ children }) => <span className="font-semibold">{children}</span>,
    h2: ({ children }) => <span className="font-semibold">{children}</span>,
    h3: ({ children }) => <span className="font-semibold">{children}</span>,
    h4: ({ children }) => <span className="font-semibold">{children}</span>,
    h5: ({ children }) => <span className="font-semibold">{children}</span>,
    h6: ({ children }) => <span className="font-semibold">{children}</span>,
    blockquote: ({ children }) => <>{children}</>,
    pre: ({ children }) => <>{children}</>,
    img: () => null,
    hr: () => null,
};

function InlineMarkdown({ children }) {
    if (typeof children !== "string" || !children) return children ?? null;
    return (
        <Markdown remarkPlugins={[remarkGfm]} components={INLINE_MD_COMPONENTS}>
            {children}
        </Markdown>
    );
}

export default function UserActionRequest({ request, onAction, isGeneratingCode }) {
    if (!request) return null;
    const { type, message } = request;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="w-full max-w-2xl"
        >
            <div className="bg-gray-50 dark:bg-gray-900/60 border border-gray-200/60 dark:border-gray-700/40 rounded-2xl rounded-tl-sm px-4 py-3">
                {type === "actions" && (
                    <ActionButtons message={message} options={request.options || []} onAction={onAction} disabled={isGeneratingCode} />
                )}
                {type === "question" && (
                    <QuestionStepper message={message} questions={request.questions || []} onAction={onAction} disabled={isGeneratingCode} />
                )}
            </div>
        </motion.div>
    );
}

function ActionButtons({ message, options, onAction, disabled }) {
    return (
        <>
            <p className="text-sm text-gray-700 dark:text-gray-200 mb-3">
                <InlineMarkdown>{message}</InlineMarkdown>
            </p>
            <div className="flex flex-wrap gap-2">
                {options.map((opt, i) => (
                    <Button
                        key={opt.value}
                        variant={i === 0 ? "default" : "outline"}
                        size="sm"
                        onClick={() => onAction(`userAction:${JSON.stringify({ value: opt.value, action: opt.action })}`)}
                        disabled={disabled}
                        className="active:scale-[0.97] transition-transform"
                    >
                        <InlineMarkdown>{opt.label}</InlineMarkdown>
                    </Button>
                ))}
            </div>
        </>
    );
}

function QuestionStepper({ message, questions, onAction, disabled }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState(() =>
        questions.map(() => ({ selected: [], otherActive: false, otherText: "" }))
    );
    const [direction, setDirection] = useState(1);
    const total = questions.length;
    const current = questions[step];
    const answer = answers[step];

    if (!current || !answer) return null;

    const updateAnswer = useCallback((updater) => {
        setAnswers((prev) => prev.map((a, i) => (i === step ? updater(a) : a)));
    }, [step]);

    const submitAll = useCallback(() => {
        let derivedAction;
        const lines = questions.map((q, i) => {
            const a = answers[i];
            const values = [...a.selected];
            const otherSubmitted = a.otherActive && a.otherText.trim();
            if (otherSubmitted) values.push(a.otherText.trim());
            const hasExitOpt = q.options?.some((o) => o.action === "exit_plan");
            for (const val of a.selected) {
                const matched = q.options?.find((o) => o.value === val);
                if (matched?.action === "exit_plan") derivedAction = "exit_plan";
            }
            if (otherSubmitted && hasExitOpt) derivedAction = "exit_plan";
            return `${q.question}\n${values.join(", ")}`;
        });
        const payload = { value: lines.join("\n\n") };
        if (derivedAction) payload.action = derivedAction;
        onAction(`userAction:${JSON.stringify(payload)}`);
    }, [questions, answers, onAction]);

    const handleSingleSelect = useCallback((val, action) => {
        setAnswers((prev) => prev.map((a, i) => (i === step ? { ...a, selected: [val] } : a)));
        if (total === 1) {
            const payload = { value: val };
            if (action) payload.action = action;
            onAction(`userAction:${JSON.stringify(payload)}`);
        } else {
            setDirection(1);
            setTimeout(() => {
                if (step < total - 1) setStep((s) => s + 1);
                else submitAll();
            }, 150);
        }
    }, [step, total, onAction, submitAll]);

    const handleMultiConfirm = useCallback(() => {
        const a = answers[step];
        const hasSelection = a.selected.length > 0 || (a.otherActive && a.otherText.trim());
        if (!hasSelection) return;
        if (step < total - 1) {
            setDirection(1);
            setStep((s) => s + 1);
        } else {
            submitAll();
        }
    }, [step, total, answers, submitAll]);

    const goBack = useCallback(() => {
        if (step > 0) {
            setDirection(-1);
            setStep((s) => s - 1);
        }
    }, [step]);

    const slideVariants = {
        enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
    };

    return (
        <div>
            {total > 1 && (
                <div className="flex items-center gap-2.5 mb-2.5">
                    <div className="flex gap-1.5">
                        {questions.map((_, i) => (
                            <div
                                key={i}
                                className={`h-1 rounded-full transition-all duration-300 ${
                                    i === step
                                        ? "w-5 bg-blue-500 dark:bg-blue-400"
                                        : i < step
                                            ? "w-1.5 bg-blue-300 dark:bg-blue-600"
                                            : "w-1.5 bg-gray-200 dark:bg-gray-700"
                                }`}
                            />
                        ))}
                    </div>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                        {step + 1}/{total}
                    </span>
                </div>
            )}

            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={step}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                >
                    <div className="flex items-center gap-2.5 mb-3">
                        <span className="text-[10px] font-bold uppercase tracking-[0.08em] px-2 py-[3px] rounded-md bg-blue-500/10 dark:bg-blue-400/15 text-blue-600 dark:text-blue-400">
                            {current.header}
                        </span>
                    </div>

                    <p className="text-[13px] font-medium text-gray-800 dark:text-gray-100 mb-3 leading-relaxed">
                        <InlineMarkdown>{current.question}</InlineMarkdown>
                    </p>

                    <div className="space-y-1.5">
                        {(current.options || []).map((opt) => (
                            <OptionCard
                                key={opt.value}
                                opt={opt}
                                isSelected={answer.selected.includes(opt.value)}
                                multiSelect={current.multiSelect}
                                disabled={disabled}
                                onSelect={() => {
                                    if (current.multiSelect) {
                                        updateAnswer((a) => ({
                                            ...a,
                                            selected: a.selected.includes(opt.value)
                                                ? a.selected.filter((v) => v !== opt.value)
                                                : [...a.selected, opt.value],
                                        }));
                                    } else {
                                        handleSingleSelect(opt.value, opt.action);
                                    }
                                }}
                            />
                        ))}

                        <OtherOption
                            active={answer.otherActive}
                            text={answer.otherText}
                            multiSelect={current.multiSelect}
                            disabled={disabled}
                            t={t}
                            onToggle={() => {
                                if (current.multiSelect) {
                                    updateAnswer((a) => ({ ...a, otherActive: !a.otherActive }));
                                } else {
                                    updateAnswer((a) => ({ ...a, otherActive: true }));
                                }
                            }}
                            onTextChange={(val) => updateAnswer((a) => ({ ...a, otherText: val }))}
                            onSubmit={() => {
                                if (!answer.otherText.trim()) return;
                                const exitAction = current.options?.find(
                                    (o) => o.action === "exit_plan"
                                )?.action;
                                handleSingleSelect(
                                    answer.otherText.trim(),
                                    exitAction
                                );
                            }}
                        />
                    </div>

                    <div className="flex items-center justify-between mt-4">
                        <div>
                            {step > 0 && (
                                <button
                                    onClick={goBack}
                                    className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                >
                                    {t("common.back")}
                                </button>
                            )}
                        </div>

                        {current.multiSelect && (
                            <Button
                                size="sm"
                                onClick={handleMultiConfirm}
                                disabled={disabled || (answer.selected.length === 0 && !(answer.otherActive && answer.otherText.trim()))}
                                className="active:scale-[0.97] transition-transform gap-1.5"
                            >
                                {step < total - 1 ? t("common.next") : t("chatMessages.submitAnswer")}
                                {step < total - 1 && <ArrowRight className="h-3.5 w-3.5" />}
                            </Button>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}

function OptionCard({ opt, isSelected, multiSelect, disabled, onSelect }) {
    return (
        <button
            className={`group w-full text-left px-3.5 py-3 rounded-xl border transition-all duration-150 active:scale-[0.98] disabled:opacity-50 ${
                isSelected
                    ? "border-blue-400 dark:border-blue-500 bg-white dark:bg-blue-950/30 shadow-sm shadow-blue-100 dark:shadow-none"
                    : "border-gray-200/80 dark:border-gray-700/60 bg-white/60 dark:bg-gray-900/40 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-white dark:hover:bg-gray-900/60"
            }`}
            onClick={onSelect}
            disabled={disabled}
        >
            <div className="flex items-start gap-2.5">
                {multiSelect && <SelectCheckbox checked={isSelected} />}
                <div className="min-w-0">
                    <span className={`text-sm font-medium block ${
                        isSelected ? "text-blue-700 dark:text-blue-300" : "text-gray-800 dark:text-gray-200"
                    }`}>
                        <InlineMarkdown>{opt.label}</InlineMarkdown>
                    </span>
                    {opt.description && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 block leading-relaxed">
                            <InlineMarkdown>{opt.description}</InlineMarkdown>
                        </span>
                    )}
                </div>
                {!multiSelect && (
                    <div className={`ml-auto shrink-0 mt-0.5 transition-opacity ${
                        isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-40"
                    }`}>
                        <ArrowRight className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400" />
                    </div>
                )}
            </div>
        </button>
    );
}

function OtherOption({ active, text, multiSelect, disabled, t, onToggle, onTextChange, onSubmit }) {
    const showToggleButton = !active || multiSelect;
    const cardClasses =
        "w-full rounded-xl border transition-all duration-150 disabled:opacity-50 border-gray-200/80 dark:border-gray-700/60 bg-white/60 dark:bg-gray-900/40";
    return (
        <>
            {showToggleButton && (
                <button
                    className={`${cardClasses} text-left px-3.5 py-3 active:scale-[0.98] hover:border-gray-300 dark:hover:border-gray-600 hover:bg-white dark:hover:bg-gray-900/60`}
                    onClick={onToggle}
                    disabled={disabled}
                >
                    <div className="flex items-center gap-2.5">
                        {multiSelect && <SelectCheckbox checked={active} />}
                        <span className="text-sm text-gray-400 dark:text-gray-500">{t("chatMessages.otherOption")}</span>
                    </div>
                </button>
            )}

            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden"
                    >
                        <div className={`${cardClasses} flex items-center gap-1 px-3.5 py-1.5 ${showToggleButton ? "mt-1.5" : ""} focus-within:border-blue-400 dark:focus-within:border-blue-500`}>
                            <input
                                type="text"
                                placeholder={t("chatMessages.otherPlaceholder")}
                                value={text}
                                onChange={(e) => onTextChange(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && !multiSelect && onSubmit()}
                                disabled={disabled}
                                autoFocus
                                className="flex-1 bg-transparent text-sm border-0 outline-none focus:outline-none focus:ring-0 px-0 py-1.5 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                            />
                            {!multiSelect && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={onSubmit}
                                    disabled={disabled || !text.trim()}
                                    className="active:scale-[0.97] transition-transform shrink-0 h-7 w-7 p-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
                                >
                                    <Send className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function SelectCheckbox({ checked }) {
    return (
        <div className={`w-4 h-4 rounded-[5px] border-[1.5px] flex items-center justify-center shrink-0 mt-0.5 transition-all duration-150 ${
            checked ? "bg-blue-500 border-blue-500 scale-105" : "border-gray-300 dark:border-gray-600"
        }`}>
            {checked && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
        </div>
    );
}
